import { SHOPPING_AREAS } from './locationService';

const SHOP_KEY = 'walkin_admin_shops';
const INVOICE_KEY = 'walkin_admin_invoices';
const TOKEN_LOG_KEY = 'walkin_admin_token_log';
const AREA_OVERRIDE_KEY = 'walkin_admin_areas';
const ACTIVITY_KEY = 'walkin_admin_activity';

export const AREA_CODES = {
  amsterdam_zuid_as: 'AZA',
  amsterdamse_poort: 'WAP',
  alexandrium: 'ALR',
  citymall_almere: 'CMA',
  gelderlandplein: 'WGP',
  stadshart: 'SHL',
  hoge_schie: 'HSR',
};

function rand(n) {
  return Math.floor(Math.random() * n);
}

function daysAgoIso(days) {
  const d = new Date();
  d.setDate(d.getDate() - days);
  return d.toISOString();
}

function locationCodeFor(area) {
  const matchEntry = SHOPPING_AREAS.find((a) => a.name === area);
  const prefix = matchEntry ? AREA_CODES[matchEntry.id] : 'GEN';
  return `${prefix}-${4000 + rand(5999)}`;
}

function userIdFor(area) {
  const matchEntry = SHOPPING_AREAS.find((a) => a.name === area);
  const prefix = matchEntry ? AREA_CODES[matchEntry.id] : 'GEN';
  return `USR-${prefix}-${100000 + rand(899999)}`;
}

function buildShop(seed) {
  const tokensAvailable = Math.max(0, seed.tokensPurchased - seed.tokensUsed);
  return {
    id: `shop-${seed.email}`,
    email: seed.email,
    shopName: seed.shopName,
    area: seed.area,
    locationCode: locationCodeFor(seed.area),
    userId: userIdFor(seed.area),
    subscription: seed.subscription,
    tokensPurchased: seed.tokensPurchased,
    tokensUsed: seed.tokensUsed,
    tokensAvailable,
    status: seed.status || 'actief',
    createdAt: daysAgoIso(seed.daysOld),
    lastActivityAt: daysAgoIso(seed.lastActiveDays),
    phone: '+31 6 1234 5678',
    address: 'Voorbeeldstraat 1',
    city: seed.area.split(' ').pop() || 'Amsterdam',
  };
}

export function getShops() {
  let adminShops = [];
  let registered = [];
  try { adminShops = JSON.parse(localStorage.getItem(SHOP_KEY) || '[]'); } catch { adminShops = []; }
  try { registered = JSON.parse(localStorage.getItem('walkin_shops') || '[]'); } catch { registered = []; }
  const seen = new Set();
  return [...adminShops, ...registered].filter((s) => {
    const email = (s.email || '').toLowerCase();
    if (!email || seen.has(email)) return false;
    seen.add(email);
    return true;
  });
}

export function saveShops(shops) {
  localStorage.setItem(SHOP_KEY, JSON.stringify(shops));
  window.dispatchEvent(new Event('walkin-admin-shops-change'));
}

export function getShop(id) {
  return getShops().find((s) => s.id === id) || null;
}

export function updateShop(id, patch) {
  const current = getShop(id);
  const emailLower = (current?.email || '').toLowerCase();
  for (const key of ['walkin_shops', 'walkin_admin_shops']) {
    let list = [];
    try { list = JSON.parse(localStorage.getItem(key) || '[]'); } catch { list = []; }
    const next = list.map((s) => {
      const matches = s.id === id || (emailLower && (s.email || '').toLowerCase() === emailLower);
      return matches ? { ...s, ...patch } : s;
    });
    localStorage.setItem(key, JSON.stringify(next));
  }
  window.dispatchEvent(new Event('walkin-admin-shops-change'));
  return getShop(id);
}

export function addShop(data) {
  const shop = buildShop({
    daysOld: 0,
    lastActiveDays: 0,
    tokensPurchased: 0,
    tokensUsed: 0,
    subscription: 'tokens',
    ...data,
  });
  const next = [shop, ...getShops()];
  saveShops(next);
  return shop;
}

export function generateTempPassword() {
  const chunk = () => Math.random().toString(36).slice(2, 6).toUpperCase();
  return `${chunk()}-${chunk()}`;
}

// ── Invoices ──────────────────────────────────────────────────────────────
const PACKAGE_PRICES = {
  Starter: 29,
  Growth: 69,
  Unlimited: 99,
};

function generateInvoices(shops) {
  const out = [];
  let counter = 1;
  shops.forEach((shop) => {
    const months = shop.subscription === 'unlimited' ? 3 : 2;
    for (let i = 0; i < months; i++) {
      const pkg =
        shop.subscription === 'unlimited'
          ? 'Unlimited'
          : Math.random() < 0.4
            ? 'Growth'
            : 'Starter';
      const amount = PACKAGE_PRICES[pkg];
      const vat = Math.round(amount * 0.21 * 100) / 100;
      const total = Math.round((amount + vat) * 100) / 100;
      out.push({
        id: `inv-${counter}`,
        number: `WLK-2026-${String(100000 + counter).slice(1)}`,
        shopId: shop.id,
        shopName: shop.shopName,
        type: pkg,
        amount,
        vat,
        total,
        date: daysAgoIso(i * 30 + rand(7)),
        status: i === 0 && Math.random() < 0.25 ? 'Openstaand' : 'Betaald',
      });
      counter += 1;
    }
  });
  return out.sort((a, b) => new Date(b.date) - new Date(a.date));
}

export function getInvoices() {
  try {
    return JSON.parse(localStorage.getItem(INVOICE_KEY) || '[]');
  } catch {
    return [];
  }
}

export function regenerateInvoices() {
  // Only used as a developer convenience — explicitly regenerate mocks
  const seeded = generateInvoices(getShops());
  localStorage.setItem(INVOICE_KEY, JSON.stringify(seeded));
  return seeded;
}

// ── Token log ─────────────────────────────────────────────────────────────
export function getTokenLog() {
  try {
    return JSON.parse(localStorage.getItem(TOKEN_LOG_KEY) || '[]');
  } catch {
    return [];
  }
}

export function appendTokenLog(entry) {
  const log = getTokenLog();
  const next = [{ ...entry, at: new Date().toISOString() }, ...log].slice(0, 200);
  localStorage.setItem(TOKEN_LOG_KEY, JSON.stringify(next));
}

// ── Activity log ──────────────────────────────────────────────────────────
export function getActivity() {
  try {
    return JSON.parse(localStorage.getItem(ACTIVITY_KEY) || '[]');
  } catch {
    return [];
  }
}

export function logActivity(action, meta = {}) {
  const list = getActivity();
  const entry = {
    id: 'act-' + Date.now() + '-' + Math.random().toString(36).slice(2, 6),
    action,
    meta,
    at: new Date().toISOString(),
    admin: sessionStorage.getItem('walkin_admin_name') || 'Admin',
  };
  const next = [entry, ...list].slice(0, 100);
  localStorage.setItem(ACTIVITY_KEY, JSON.stringify(next));
}

// ── Area overrides ────────────────────────────────────────────────────────
export function getAreas() {
  try {
    const raw = localStorage.getItem(AREA_OVERRIDE_KEY);
    if (raw) return JSON.parse(raw);
  } catch { /* ignore */ }
  const seeded = SHOPPING_AREAS.map((a) => ({
    id: a.id,
    name: a.name,
    prefix: AREA_CODES[a.id] || 'GEN',
    city: a.name.split(' ').pop(),
    lat: a.lat,
    lng: a.lng,
    radius: a.radius,
    status: 'actief',
    description: '',
  }));
  localStorage.setItem(AREA_OVERRIDE_KEY, JSON.stringify(seeded));
  return seeded;
}

export function saveAreas(areas) {
  localStorage.setItem(AREA_OVERRIDE_KEY, JSON.stringify(areas));
  window.dispatchEvent(new Event('walkin-admin-areas-change'));
}

export function addArea(area) {
  const next = [...getAreas(), { ...area, id: area.id || 'area-' + Date.now() }];
  saveAreas(next);
  return next;
}

export function updateArea(id, patch) {
  const next = getAreas().map((a) => (a.id === id ? { ...a, ...patch } : a));
  saveAreas(next);
  return next.find((a) => a.id === id);
}

export function deleteArea(id) {
  saveAreas(getAreas().filter((a) => a.id !== id));
}

// ── Aggregate helpers ─────────────────────────────────────────────────────
export function getAllDeals() {
  try {
    return JSON.parse(localStorage.getItem('walkin_shop_deals') || '[]');
  } catch {
    return [];
  }
}

export function downloadCsv(filename, rows) {
  if (!rows.length) return;
  const headers = Object.keys(rows[0]);
  const escape = (val) => {
    const s = val == null ? '' : String(val);
    return /[",\n]/.test(s) ? '"' + s.replace(/"/g, '""') + '"' : s;
  };
  const csv = [
    headers.join(','),
    ...rows.map((r) => headers.map((h) => escape(r[h])).join(',')),
  ].join('\n');
  const blob = new Blob([csv], { type: 'text/csv;charset=utf-8;' });
  const url = URL.createObjectURL(blob);
  const a = document.createElement('a');
  a.href = url;
  a.download = filename;
  a.click();
  URL.revokeObjectURL(url);
}
